import React, {
  useLayoutEffect, useMemo, useRef, useState
} from 'react';
import ReactTags from 'react-tag-autocomplete';

import classNames from 'classnames';

import assign from 'lodash/assign';
import isFunction from 'lodash/isFunction';
import uniq from 'lodash/uniq';

import hashCode from '../../utils/hash_code';

import Button from '../common/Button/Button';

export const TAG_COLOR_CLASSES = [
  'bg-gray-400',
  'bg-red-400',
  'bg-yellow-400',
  'bg-green-400',
  'bg-blue-400',
  'bg-indigo-400',
  'bg-purple-400',
  'bg-pink-400',
];

const TagComponent = ({
  tag, onDelete, colorClassForTag, onDeleteTag
}) => (
  <span
    className={classNames(
      'uppercase text-white font-bold pl-4 pr-3 py-1 text-xs mr-2 rounded-lg flex justify-center items-center',
      colorClassForTag(tag.name),
    )}
  >
    {tag.name}
    {
      isFunction(onDeleteTag) && (
        <Button
          variant="close"
          textColor="white"
          hoverTextColor="border-dark"
          activeTextColor="border-darker"
          onClick={onDelete}
        />
      )
    }
  </span>
);

const ContentTags = ({ tags, onAddTag, onDeleteTag }) => {
  const containerRef = useRef(null);
  const [isNewTagFieldFocused, setIsNewTagFieldFocused] = useState(false);

  const noDupeTags = uniq(tags);

  const handleNewTagButtonClick = () => {
    setIsNewTagFieldFocused(true);
  };

  const handleAddTag = (newTag) => {
    if (!onAddTag || tags.map(tag => tag.toLowerCase()).includes(newTag.name.toLowerCase())) {
      return;
    }
    onAddTag(newTag.name);
  };

  const handleDeleteTag = (deletedTagIndex) => {
    const tag = tags[deletedTagIndex];
    if (!onDeleteTag || !tag) {
      return;
    }

    onDeleteTag(tag);
  };

  const handleTagFieldBlur = () => {
    setIsNewTagFieldFocused(false);
  };

  useLayoutEffect(() => {
    if (!isNewTagFieldFocused || !containerRef.current) {
      return;
    }

    containerRef.current.querySelector('.react-tags__search-input').focus();
  }, [isNewTagFieldFocused]);

  const colorClassForTag = tag => TAG_COLOR_CLASSES[Math.abs(hashCode(tag)) % TAG_COLOR_CLASSES.length];

  const tagClasses = useMemo(() => assign(
    {
      root: 'react-tags',
      rootFocused: 'is-focused',
      selected: 'react-tags__selected',
      selectedTag: 'react-tags__selected-tag',
      selectedTagName: 'react-tags__selected-tag-name',
      search: 'react-tags__search',
      searchWrapper: 'react-tags__search-wrapper',
      searchInput: 'react-tags__search-input',
      suggestions: 'react-tags__suggestions',
      suggestionActive: 'is-active',
      suggestionDisabled: 'is-disabled',
      suggestionPrefix: 'react-tags__suggestion-prefix',
    },
    {
      root: 'react-tags-root flex justify-start items-center',
      searchInput: classNames('react-tags__search-input pl-2 min-w-min', {
        hidden: !isNewTagFieldFocused,
      }),
      selected: 'flex justify-start items-center flex-wrap gap-y-1',
    },
  ), [isNewTagFieldFocused]);

  return (
    <div className="flex justify-start items-center" ref={containerRef}>
      <ReactTags
        classNames={tagClasses}
        tags={noDupeTags?.map(tag => ({ id: tag, name: tag })) || []}
        placeholderText="New tag"
        onDelete={handleDeleteTag}
        allowBackspace={false}
        onAddition={handleAddTag}
        onBlur={handleTagFieldBlur}
        // Cannot pass in TagComponent with props without defining component 
        // eslint-disable-next-line
        tagComponent={({ tag, onDelete }) => <TagComponent tag={tag} onDelete={onDelete} colorClassForTag={colorClassForTag} onDeleteTag={onDeleteTag} />}
        allowNew
      />
      {
        (isFunction(onAddTag) && !isNewTagFieldFocused) && (
          <Button
            variant="add"
            textColor="white"
            hoverTextColor="border-dark"
            activeTextColor="border-darker"
            onClick={handleNewTagButtonClick}
          />
        )
      }
    </div>
  );
};
export default ContentTags;
